import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { toast } from 'sonner';

const contactSchema = z.object({
  name: z.string().min(2, 'Name muss mindestens 2 Zeichen lang sein'),
  email: z.string().email('Bitte geben Sie eine gültige E-Mail-Adresse ein'),
  company: z.string().min(2, 'Firmenname muss mindestens 2 Zeichen lang sein'),
  phone: z.string().optional(),
  subject: z.string().min(1, 'Bitte wählen Sie ein Thema'),
  message: z.string().min(10, 'Nachricht muss mindestens 10 Zeichen lang sein'),
});

type ContactForm = z.infer<typeof contactSchema>;

export default function Contact() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ContactForm>({
    resolver: zodResolver(contactSchema),
  });

  const onSubmit = async (data: ContactForm) => {
    setIsSubmitting(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          company: data.company,
          phone: data.phone || '',
          subject: data.subject,
          message: data.message,
        }),
      });
      if (!response.ok) {
        throw new Error('Fehler beim Senden');
      }
      toast.success('Vielen Dank! Ihre Nachricht wurde erfolgreich gesendet. Wir melden uns innerhalb von 24 Stunden.');
      reset();
    } catch {
      toast.error('Nachricht konnte nicht gesendet werden. Bitte schreiben Sie direkt an kontakt@salesforge.de');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'E-Mail',
      content: 'kontakt@salesforge.de',
      link: 'mailto:kontakt@salesforge.de',
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '0163 674 333 2',
      link: 'tel:+4916367433332',
    },
    {
      icon: MapPin,
      title: 'Adresse',
      content: 'Ferdinand-Wallbrecht-Str. 55, 30163 Hannover',
      link: 'https://maps.google.com/?q=Ferdinand-Wallbrecht-Strasse+55+30163+Hannover',
    },
    {
      icon: Clock,
      title: 'Öffnungszeiten',
      content: 'Mo-Fr: 9:00 - 18:00 Uhr',
      link: null,
    },
  ];

  const subjects = [
    'Sales Audit Anfrage',
    'Beratungsgespräch',
    'Partnerschaft',
    'Support',
    'Sonstiges',
  ];

  return (
    <Layout>
      <div className="flex-1">
        {/* Hero Section */}
        <section className="relative py-24 overflow-hidden bg-primary text-primary-foreground">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-b from-primary-foreground/20 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px]" />
          </div>
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-primary-foreground">
                Kontaktieren Sie uns
              </h1>
              <p className="text-xl text-primary-foreground/80 mb-8">
                Haben Sie Fragen zu unserem Sales Audit Tool? Wir sind für Sie da.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow duration-200">
                    <CardContent className="p-6 text-center">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <info.icon className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-semibold mb-2">{info.title}</h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-sm text-muted-foreground hover:text-primary transition-colors"
                          target={info.link.startsWith('http') ? '_blank' : undefined}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                          {info.content}
                        </a>
                      ) : (
                        <p className="text-sm text-muted-foreground">{info.content}</p>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Contact Form */}
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl">Senden Sie uns eine Nachricht</CardTitle>
                    <CardDescription>
                      Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name *</Label>
                          <Input
                            id="name"
                            placeholder="Max Mustermann"
                            {...register('name')}
                            className={errors.name ? 'border-destructive' : ''}
                          />
                          {errors.name && (
                            <p className="text-sm text-destructive">{errors.name.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">E-Mail *</Label>
                          <Input
                            id="email"
                            type="email"
                            placeholder="max@firma.de"
                            {...register('email')}
                            className={errors.email ? 'border-destructive' : ''}
                          />
                          {errors.email && (
                            <p className="text-sm text-destructive">{errors.email.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="company">Firma *</Label>
                          <Input
                            id="company"
                            placeholder="Firma GmbH"
                            {...register('company')}
                            className={errors.company ? 'border-destructive' : ''}
                          />
                          {errors.company && (
                            <p className="text-sm text-destructive">{errors.company.message}</p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="phone">Telefon</Label>
                          <Input
                            id="phone"
                            type="tel"
                            placeholder="+49 123 456789"
                            {...register('phone')}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">Thema *</Label>
                        <Select onValueChange={(value) => setValue('subject', value)}>
                          <SelectTrigger className={errors.subject ? 'border-destructive' : ''}>
                            <SelectValue placeholder="Bitte wählen Sie ein Thema" />
                          </SelectTrigger>
                          <SelectContent>
                            {subjects.map((subject) => (
                              <SelectItem key={subject} value={subject}>
                                {subject}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.subject && (
                          <p className="text-sm text-destructive">{errors.subject.message}</p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">Nachricht *</Label>
                        <Textarea
                          id="message"
                          placeholder="Beschreiben Sie Ihr Anliegen..."
                          rows={6}
                          {...register('message')}
                          className={errors.message ? 'border-destructive' : ''}
                        />
                        {errors.message && (
                          <p className="text-sm text-destructive">{errors.message.message}</p>
                        )}
                      </div>

                      <Button
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span>
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Nachricht senden
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      Häufig gestellte Fragen
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-semibold mb-2">Wie lange dauert das Audit?</h4>
                      <p className="text-sm text-muted-foreground">
                        Das Sales Audit dauert ca. 20 Minuten. Sie erhalten sofort nach Abschluss 
                        Ihre Ergebnisse und Handlungsempfehlungen.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Ist das Audit wirklich kostenlos?</h4>
                      <p className="text-sm text-muted-foreground">
                        Ja, das Sales Audit Tool ist 100% kostenlos und unverbindlich. 
                        Sie erhalten eine vollständige Analyse ohne versteckte Kosten.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Was passiert mit meinen Daten?</h4>
                      <p className="text-sm text-muted-foreground">
                        Ihre Daten werden vertraulich behandelt und ausschließlich für die 
                        Audit-Auswertung verwendet. Wir geben keine Daten an Dritte weiter.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Bieten Sie auch Beratung an?</h4>
                      <p className="text-sm text-muted-foreground">
                        Ja, nach dem Audit können Sie optional ein kostenloses Strategiegespräch 
                        buchen, um Ihre Ergebnisse zu besprechen und nächste Schritte zu planen.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold mb-2">Bevorzugen Sie ein Telefonat?</h3>
                    <p className="text-muted-foreground mb-4">
                      Rufen Sie uns direkt an oder buchen Sie einen Termin für ein 
                      unverbindliches Beratungsgespräch.
                    </p>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full"
                    >
                      <a href="tel:+4916367433332">
                        <Phone className="w-4 h-4 mr-2" />
                        0163 674 333 2
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Map Section (Placeholder) */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card>
                <CardContent className="p-0">
                  <div className="w-full h-96 bg-gradient-to-br from-muted to-muted/50 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="w-12 h-12 text-primary mx-auto mb-4" />
                      <p className="text-lg font-semibold">Salesforge</p>
                      <p className="text-muted-foreground">Ferdinand-Wallbrecht-Str. 55</p>
                      <p className="text-muted-foreground">30163 Hannover</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
